import { MoreVertical } from "lucide-react";
import { useEffect, useRef, useState, type JSX, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/shared/lib/cn";

export interface RowAction {
  key: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  /** When true the item renders dimmed and non-interactive; onSelect never fires. */
  disabled?: boolean;
  /** Surfaced on `title` + `aria-disabled` — explains why a disabled item is blocked. */
  tooltip?: string;
  onSelect: () => void;
}

interface RowActionMenuProps {
  actions: RowAction[];
  label?: string;
}

const MENU_WIDTH = 188;

/**
 * Always-visible "•••" trigger + a popover menu rendered in a portal with fixed
 * positioning, so it escapes the table's `overflow-x-auto` clipping context.
 */
export function RowActionMenu({ actions, label = "Row actions" }: RowActionMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const openMenu = (): void => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      setPos({
        top: r.bottom + 6,
        left: Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
      });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    // Opening the trigger (which lives in a sticky cell of a horizontally
    // scrollable table) can emit a focus-scroll in the same tick. Ignore
    // scrolls fired immediately after opening so the menu doesn't close on
    // itself; genuine user scrolls afterward still dismiss it.
    const openedAt = Date.now();
    const onDoc = (e: MouseEvent): void => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node))
        return;
      setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = (): void => {
      if (Date.now() - openedAt < 250) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid="row-action-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className="grid h-8 w-8 place-items-center rounded-lg border border-transparent text-[var(--text-subtle)] opacity-60 transition hover:border-[var(--premium-border-strong)] hover:bg-white hover:text-[var(--text-primary)] hover:opacity-100 hover:shadow-[var(--sh-xs)] focus-ring"
      >
        <MoreVertical className="h-[17px] w-[17px]" aria-hidden />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: "fixed", top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="z-[80] rounded-xl border border-[var(--premium-border)] bg-white p-1.5 shadow-[var(--sh-pop)]"
          >
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                data-testid={`row-action-${a.key}`}
                disabled={a.disabled}
                aria-disabled={a.disabled}
                {...(a.tooltip ? { title: a.tooltip } : {})}
                onClick={() => {
                  if (a.disabled) return;
                  a.onSelect();
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition",
                  a.disabled ? "cursor-not-allowed opacity-40" : "hover:bg-[var(--canvas)]",
                  a.danger ? "text-[var(--intent-danger-fg)]" : "text-[var(--text-primary)]",
                )}
              >
                {a.icon}
                {a.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
